import * as React from 'react';
import { observer } from 'mobx-react';
import {
    ITranslationsCollection,
    addConfig,
    addContext,
    configConsumer,
    addTranslation,
    translationConsumer
} from '@estee/elc-service';
import { Breakpoints } from '@estee/elc-base-theme';
import { Heading2 } from '@estee/elc-typography';
import { classes } from '../../internal/utils/Classes';
import { ProductCrossSellViewController } from '../../internal/controllers/ProductCrossSellViewController';
import { CartLimitReachedModal } from '../../internal/views/cart-limit-modal/CartLimitReachedModal';
import { ProductCarousel } from '../../internal/views/product-carousel/ProductCarousel';
import { mergeConfigs } from '../../internal/utils/ConfigUtils';
import { IOrderConfigCollection } from 'src/internal/interfaces/IOrderableComponents';
import { IFeatures } from '~constants/Features';
import { ICoreProduct } from '~api/interfaces/IProduct';
import { IItem } from '~api/interfaces/IItem';
import { IParamsById } from '~api/interfaces/IParamsById';

export interface IGridBreakpoints {
    numColumnsMobile: number;
    numColumnsDesktop: number;
    numColumnsLargeScreen: number;
    numColumnsExtraLargeScreen: number;
}

export interface IProductCrossSellState {
    products: ICoreProduct[];
}

export interface IProductCrossSellConfig {
    items?: IItem[];
    paramsById?: IParamsById;
    viewableInventoryStatuses?: string[];
    productGridBreakpoints: IGridBreakpoints;
    crossSellsCustomHeader: string;
    hideLoader?: boolean;
    componentElementsOrderConfig?: { [key: string]: IOrderConfigCollection };
    features: IFeatures;
    renderMethod: string;
}

export interface IProductCrossSell {
    className?: string;
    translations?: ITranslationsCollection;
    config: IProductCrossSellConfig;
    viewController: ProductCrossSellViewController;
    state?: IProductCrossSellState;
}

@addContext()
@addConfig(['prodcatConfig', 'features'], mergeConfigs)
@configConsumer([
    'crossSellsCustomHeader',
    'productGridBreakpoints',
    'hideLoader',
    'paramsById',
    'items',
    'componentElementsOrderConfig',
    'features'
])
@addTranslation(['crossSellsTitle'])
@translationConsumer(['crossSellsTitle'])
@observer
class ProductCrossSell extends React.Component<IProductCrossSell> {
    public static displayName: string;

    public async componentDidMount() {
        await this.props.viewController.loaded(this.props);
    }

    private get productCarouselProps() {
        const { productGridBreakpoints, renderMethod } = this.props.config;

        const {
            numColumnsMobile,
            numColumnsDesktop,
            numColumnsLargeScreen,
            numColumnsExtraLargeScreen
        } = productGridBreakpoints;

        const crossSellSettings = {
            dots: false,
            infinite: false,
            slidesToShow: numColumnsExtraLargeScreen,
            slidesToScroll: 1,
            responsive: [
                {
                    breakpoint: Breakpoints.extraLargeScreen,
                    settings: {
                        slidesToShow: numColumnsLargeScreen,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false,
                        arrows: true
                    }
                },
                {
                    breakpoint: Breakpoints.largeScreen,
                    settings: {
                        slidesToShow: numColumnsDesktop,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false,
                        arrows: true
                    }
                },
                {
                    breakpoint: Breakpoints.desktop,
                    settings: {
                        slidesToShow: numColumnsMobile,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false,
                        arrows: true
                    }
                }
            ]
        };

        return {
            products: this.props.viewController.data.crossSellProducts,
            forceLazyImageLoading: renderMethod !== 'ondemand',
            carouselSettings: crossSellSettings
        };
    }

    private get crossSellHeader() {
        const { crossSellsCustomHeader } = this.props.config;
        const { crossSellsTitle: defaultHeader } = this.props
            .translations as ITranslationsCollection;

        return crossSellsCustomHeader || defaultHeader;
    }

    public render() {
        if (!this.props.viewController.isLoaded) {
            return null;
        }

        if (!this.props.viewController.hasCrossSellProducts) {
            return <div />;
        }

        const productCrossSellHeaderClassNames = classes('product-cross-sell-header');
        const productCrossSellClassNames = classes('product-cross-sell', this.props.className);

        return (
            <>
                <Heading2 className={productCrossSellHeaderClassNames}>
                    {this.crossSellHeader}
                </Heading2>
                <ProductCarousel
                    config={this.productCarouselProps}
                    className={productCrossSellClassNames}
                />
                <CartLimitReachedModal />
            </>
        );
    }
}

ProductCrossSell.displayName = 'ProductCrossSell';

export default ProductCrossSell;
